import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, CircularProgress, makeStyles, Paper, Typography } from '@material-ui/core';
import { env } from 'config';
import { USER_OPERATIONS } from 'helper';
import { RootState } from 'interfaces';
import { loadTempTokenBigApp } from 'pages/PageRealEstate/api';
import useError from 'components/ErrorProvider/useError';
import { useGetRealEstateReports } from './hooks';
import { RealEstateReports } from './components';

const useStyles = makeStyles({
  root: {
    position: 'relative',
    maxWidth: 1536,
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    zIndex: 1000,
  },
});

interface IProps {
  realEstateId: string;
}

const View: React.FC<IProps> = ({ realEstateId }) => {
  // Global data
  const operations = useSelector((state: RootState) => state.operations);

  // Local state
  const [pageNo, setPageNo] = useState(1);
  const pageSize = 10;

  const [bigAppTokenLoading, setBigAppTokenLoading] = useState(false);

  // Styles
  const classes = useStyles();

  // Global notification
  const { addError } = useError();

  // Methods
  const handleClickBigAppNewsLink = async () => {
    setBigAppTokenLoading(true);

    loadTempTokenBigApp()
      .then(({ token }) => window.open(`${env.BIGAPP_NEWS_URL}?key=${token}`, '_blank'))
      .catch((error) => {
        addError(error.response.data.message);
      })
      .finally(() => {
        setBigAppTokenLoading(false);
      });
  };

  const { fetchReports, ...reportsState } = useGetRealEstateReports(realEstateId);

  const params = { pageNo, pageSize };
  const mapKey = JSON.stringify(
    Object.entries(params)
      .map(([, value]) => value)
      .filter(Boolean),
  );

  useEffect(() => {
    fetchReports({ params: { pageNo, pageSize } }, false);
  }, [realEstateId, pageNo, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = reportsState.data.get(mapKey) || {
    list: [],
    totalCount: 0,
  };

  return (
    <>
      <Box className={classes.root}>
        <Box sx={{ mb: 2 }}>
          {operations.includes(USER_OPERATIONS.MANAGE_BIG_APP_NEWS) && (
            <Button
              color="primary"
              variant="contained"
              disableElevation
              disabled={bigAppTokenLoading}
              onClick={handleClickBigAppNewsLink}
            >
              Новости BigApp
            </Button>
          )}
        </Box>

        {reportsState.loading ? (
          <Paper variant="outlined">
            <Box sx={{ position: 'relative', p: 5 }}>
              <Box className={classes.loader}>
                <CircularProgress />
              </Box>
            </Box>
          </Paper>
        ) : Boolean(reportsState.error) && !Boolean(data.list.length) ? (
          <Paper variant="outlined">
            <Box sx={{ position: 'relative', p: 5 }}>
              <Typography component="h3" variant="h6" align="center">
                {reportsState.error}
              </Typography>
            </Box>
          </Paper>
        ) : data.list.length ? (
          <RealEstateReports
            list={data.list}
            totalCount={data.totalCount}
            pageNo={pageNo}
            pageSize={pageSize}
            onChangePage={setPageNo}
            onRefresh={() => fetchReports({ params: { pageNo, pageSize } }, false)}
          />
        ) : (
          <Paper variant="outlined">
            <Box sx={{ position: 'relative', p: 5 }}>
              <Typography component="h3" variant="h6" align="center">
                Данные отсутствуют
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </>
  );
};

export default View;
